import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@xsolla/ui-core';
import { ChevronDownMini, ChevronUpMini, Plus } from '@xsolla/ui-icons';

import { getRandomString } from '../../../utils';
import { Preloader } from '../../ui';
import { InputWithSave, ValueWithRemove } from './inputAndRemove';
import { Label, Wrapper } from './styleUrlList';

type Props = {
  values: Array<string>;
  label: string;
  onSaveClick: (value: string) => void;
  onRemoveClick: (id: number) => void;
  allowCollapse?: boolean;
  fetching?: boolean;
  updatingList?: boolean;
  displayOnly?: boolean;
};

const UrlList = ({
  values,
  label,
  onSaveClick,
  onRemoveClick,
  allowCollapse = false,
  fetching = false,
  updatingList = false,
  displayOnly = false
}: Props): JSX.Element => {
  const intl = useIntl();
  const [currValues, setCurrentValues] = useState<Array<string>>(values);
  const [collapsed, setCollapsed] = useState<boolean>(allowCollapse);
  const [isDuplicateUrl, setIsDuplicateUrl] = useState<boolean>(false);
  const [newValue, setNewValue] = useState<string | null>(null);

  const toggleCollapse = () => setCollapsed(!collapsed);

  const handleSaveClick = () => {
    if (newValue) {
      onSaveClick(newValue);
      setNewValue(null);
    }
  };

  const handleChange = (value: string) => {
    if (values.findIndex((val) => val === value) === -1) {
      setIsDuplicateUrl(false);
    } else {
      setIsDuplicateUrl(true);
    }
    setNewValue(value);
  };

  useEffect(() => {
    if (values.length > 3) {
      setCurrentValues(values.slice(0, 3));
    } else {
      setCurrentValues(values);
    }
  }, [fetching, values]);

  useEffect(() => {
    if (collapsed) {
      setCurrentValues(values.slice(0, 3));
    } else {
      setCurrentValues(values);
    }
  }, [collapsed]);

  if (fetching) {
    return (
      <Wrapper>
        <Label>
          <div>{label.toUpperCase()}</div>
        </Label>

        <Preloader />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Label>
        <div>{label.toUpperCase()}</div>

        {allowCollapse && values.length > 3 && (
          <Button look="link" palette="muted" onClick={toggleCollapse}>
            <span>
              {intl.formatMessage({
                id: `dashboard.connections.${collapsed ? 'show' : 'hide'}-all`
              })}
            </span>
            {collapsed ? <ChevronDownMini /> : <ChevronUpMini />}
          </Button>
        )}
      </Label>

      {currValues.map((value, index) => (
        <ValueWithRemove
          key={getRandomString()}
          value={value}
          index={index}
          onRemoveClick={onRemoveClick}
          displayOnly={displayOnly}
          disabled={updatingList}
        />
      ))}

      {newValue !== null && (
        <InputWithSave
          value={newValue}
          isDuplicate={isDuplicateUrl}
          onSaveClick={handleSaveClick}
          onChange={handleChange}
          fetching={updatingList}
        />
      )}

      {newValue === null && !displayOnly && (
        <Button look="link" palette="brand" disabled={updatingList} onClick={() => setNewValue('')}>
          <Plus />
          <span>{intl.formatMessage({ id: 'dashboard.connections.add' })}</span>
        </Button>
      )}
    </Wrapper>
  );
};

export default UrlList;
